import { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import {
  Sparkles,
  Heart,
  Sun,
  MessageCircle,
  MapPin,
  Phone,
  Facebook,
  Check,
  ArrowRight,
  Dumbbell,
  GraduationCap,
  Leaf,
  Quote,
} from "lucide-react";
import { COVER_URL, LOGO_URL, FACEBOOK_URL, MESSENGER_URL, BOOKING_URL } from "@/constants/content";
import { useSiteContent } from "@/hooks/useSiteContent";
import { HOME, NAV, FORM } from "@/constants/testIds";
import MagnetModal from "@/components/MagnetModal";

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  show: { opacity: 1, y: 0, transition: { duration: 0.9, ease: [0.22, 1, 0.36, 1] } },
};
const stagger = { hidden: {}, show: { transition: { staggerChildren: 0.12 } } };

const ICONS = { Dumbbell, GraduationCap, Sparkles };

function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [0, 80]);
  return (
    <section
      id="top"
      ref={ref}
      data-testid={HOME.hero}
      className="relative grid grid-cols-1 md:grid-cols-12 gap-12 max-w-7xl mx-auto px-6 md:px-12 pt-10 md:pt-16 pb-20 md:pb-24 items-center"
    >
      <motion.div variants={stagger} initial="hidden" animate="show" className="md:col-span-7 relative z-10">
        <motion.div variants={fadeUp} className="inline-flex items-center gap-2 mb-7 px-4 py-1.5 rounded-full bg-[#F6F3EB] border border-[#D4AF37]/30 text-[#A88A2B]">
          <Sparkles className="h-3.5 w-3.5" />
          <span className="text-[11px] uppercase tracking-[0.28em]">Mozgás Éjszakája · 2026</span>
        </motion.div>
        <motion.h1 variants={fadeUp} className="font-serif-display text-[#1A2825] font-light tracking-tight leading-[1.02] text-5xl sm:text-6xl md:text-7xl">
          A harmónia <em className="not-italic text-[#2A9D8F]">belül</em> kezdődik
          <span className="block text-[#1A2825]/85">– és <span className="italic text-[#A88A2B]">kívül</span> látszik.</span>
        </motion.h1>
        <motion.p variants={fadeUp} className="mt-8 max-w-xl text-lg md:text-xl text-[#3A4D4A] font-light leading-relaxed">
          Csukd be a szemed egy pillanatra. Érezd, ahogy a vállad elenged, a légzésed lelassul.
          Itt most kezdődik valami — egy szelíd út vissza önmagadhoz, a tested ritmusához, a lelked csendjéhez.
        </motion.p>
        <motion.div variants={fadeUp} className="mt-10 flex flex-wrap items-center gap-4">
          <a
            href="#magnetek"
            data-testid={HOME.heroCta}
            className="inline-flex items-center gap-2 bg-[#2A9D8F] text-white px-8 py-4 rounded-full font-medium tracking-wide hover:bg-[#1C6B61] transition-all duration-300 shadow-[0_10px_24px_rgba(42,157,143,0.32)] hover:-translate-y-1"
          >
            Válassz ingyenes ajándékot
            <ArrowRight className="h-4 w-4" />
          </a>
          <a
            href={FACEBOOK_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 text-sm tracking-[0.18em] uppercase text-[#3A4D4A] hover:text-[#1877F2] transition-colors border-b border-[#D4AF37]/40 pb-1"
          >
            <Facebook className="h-4 w-4" /> Kövess a Facebookon
          </a>
        </motion.div>
        <motion.div variants={fadeUp} className="mt-12 flex items-center gap-6 text-xs tracking-wider text-[#758C88]">
          <div className="flex items-center gap-2"><Check className="h-3.5 w-3.5 text-[#2A9D8F]" /> 3 különböző ajándék</div>
          <div className="flex items-center gap-2"><Check className="h-3.5 w-3.5 text-[#2A9D8F]" /> Azonnali letöltés</div>
          <div className="flex items-center gap-2"><Check className="h-3.5 w-3.5 text-[#2A9D8F]" /> Bármikor leiratkozhatsz</div>
        </motion.div>
      </motion.div>

      <motion.div
        style={{ y }}
        initial={{ opacity: 0, scale: 0.96 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 1.2, ease: [0.22, 1, 0.36, 1] }}
        className="md:col-span-5 relative"
      >
        <div className="relative aspect-square w-full max-w-md mx-auto">
          <div className="absolute -inset-4 rounded-[3rem] border border-[#D4AF37]/30" />
          <div className="absolute inset-0 rounded-[3rem] overflow-hidden shadow-[0_30px_80px_-30px_rgba(28,107,97,0.35)]">
            <img src={COVER_URL} alt="TestLélekHarmónia" className="w-full h-full object-cover object-center" />
          </div>
          <div className="absolute -bottom-8 -left-8 float-soft">
            <img src={LOGO_URL} alt="" className="h-28 w-28 md:h-32 md:w-32 rounded-full ring-4 ring-white shadow-xl object-cover" />
          </div>
          <div className="absolute -top-3 -right-3 flex items-center gap-1 px-3 py-1.5 bg-white/90 backdrop-blur rounded-full border border-[#D4AF37]/40 shadow-sm">
            <Sun className="h-3.5 w-3.5 text-[#D4AF37]" />
            <span className="text-[10px] uppercase tracking-[0.25em] text-[#A88A2B]">Wellness</span>
          </div>
        </div>
      </motion.div>
    </section>
  );
}

function Magnets({ onPick }) {
  const { content } = useSiteContent();
  const MAGNETS = content.magnets;
  return (
    <section id="magnetek" data-testid={HOME.magnetsSection} className="relative max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-28">
      <div className="grid grid-cols-1 md:grid-cols-12 gap-10 mb-14 items-end">
        <div className="md:col-span-7">
          <span className="text-[11px] uppercase tracking-[0.3em] text-[#A88A2B]">3 ingyenes ajándék · 3 hangulat</span>
          <h2 className="font-serif-display text-4xl md:text-5xl font-light text-[#1A2825] mt-4 leading-[1.05]">
            Válaszd ki, ami <em className="not-italic text-[#2A9D8F]">most</em> hív téged.
          </h2>
        </div>
        <p className="md:col-span-5 text-[#3A4D4A] font-light leading-relaxed">
          Mindhárom anyag email cím megadása után <span className="text-[#1A2825] font-medium">azonnal letölthető</span>.
          A választott témakörben kapsz finoman válogatott tartalmakat — 4Life ajánlókkal és masszázs-tippekkel.
        </p>
      </div>

      <motion.div
        variants={stagger}
        initial="hidden"
        whileInView="show"
        viewport={{ once: true, margin: "-80px" }}
        className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8"
      >
        {MAGNETS.map((m, i) => {
          const Icon = ICONS[m.icon] || Sparkles;
          return (
            <motion.article
              key={m.key}
              variants={fadeUp}
              whileHover={{ y: -6 }}
              transition={{ duration: 0.4 }}
              data-testid={HOME.magnetCard(m.key)}
              className={`relative bg-white rounded-[2rem] p-7 md:p-8 border border-[#EAE5D9] shadow-[0_8px_32px_rgba(26,40,37,0.05)] flex flex-col ${
                i === 1 ? "md:translate-y-6" : ""
              }`}
            >
              <div
                className="h-12 w-12 rounded-full flex items-center justify-center mb-5"
                style={{ background: `${m.accent}15`, border: `1px solid ${m.accent}35` }}
              >
                <Icon className="h-5 w-5" style={{ color: m.accent }} />
              </div>
              <div
                className="text-[10px] uppercase tracking-[0.3em]"
                style={{ color: m.accent }}
              >
                {m.badge}
              </div>
              <h3 className="font-serif-display text-2xl md:text-3xl font-normal text-[#1A2825] mt-2 leading-tight">
                {m.title}
              </h3>
              <p className="mt-2 text-sm text-[#A88A2B] italic">{m.subtitle}</p>
              <p className="mt-4 text-[#3A4D4A] font-light leading-relaxed text-[15px]">{m.description}</p>
              <ul className="mt-5 space-y-2 text-sm text-[#3A4D4A] font-light">
                {m.bullets.map((b) => (
                  <li key={b} className="flex items-start gap-2">
                    <Check className="h-4 w-4 flex-shrink-0 mt-0.5" style={{ color: m.accent }} /> {b}
                  </li>
                ))}
              </ul>
              <button
                type="button"
                onClick={() => onPick(m.key)}
                data-testid={HOME.magnetSelect(m.key)}
                className="mt-7 inline-flex items-center justify-center gap-2 text-white px-6 py-3 rounded-full font-medium tracking-wide transition-all shadow-md hover:-translate-y-0.5"
                style={{ background: m.accent }}
              >
                Kérem ingyen <ArrowRight className="h-4 w-4" />
              </button>
            </motion.article>
          );
        })}
      </motion.div>
    </section>
  );
}

function ServicesPreview() {
  const { content } = useSiteContent();
  const MASSAGES = content.massages;
  const FOURLIFE_PRODUCTS = content.fourlife;
  return (
    <section data-testid={HOME.servicesPreview} className="relative max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-24">
      <div className="grid grid-cols-1 md:grid-cols-12 gap-10 mb-12 items-end">
        <div className="md:col-span-7">
          <span className="text-[11px] uppercase tracking-[0.3em] text-[#A88A2B]">Szolgáltatások és ajánlatok</span>
          <h2 className="font-serif-display text-4xl md:text-5xl font-light text-[#1A2825] mt-4 leading-[1.05]">
            Masszázs, vitamin, <em className="not-italic text-[#2A9D8F]">egyensúly</em>.
          </h2>
        </div>
        <p className="md:col-span-5 text-[#3A4D4A] font-light leading-relaxed">
          Klasszikus kezelések a stúdióban, és 4Life prémium csomagok otthonra — egyetlen koherens
          rendszerben, hogy a regenerációd minden napod része lehessen.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {MASSAGES.map((s) => (
          <div key={s.key} className="bg-white rounded-[1.75rem] p-7 border border-[#EAE5D9] flex flex-col">
            <div className="text-[10px] uppercase tracking-[0.25em] text-[#2A9D8F]">Masszázs</div>
            <h3 className="font-serif-display text-xl md:text-2xl text-[#1A2825] mt-2 leading-snug">{s.title}</h3>
            <p className="text-sm text-[#A88A2B] italic mt-1">{s.short}</p>
            <p className="mt-3 text-sm text-[#3A4D4A] font-light leading-relaxed flex-1">{s.description}</p>
            <div className="mt-5 text-xs uppercase tracking-wider text-[#758C88]">{s.duration} · {s.price}</div>
            <a
              href={BOOKING_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-4 inline-flex items-center justify-center gap-2 bg-[#2A9D8F] hover:bg-[#22857a] text-white px-5 py-2.5 rounded-full text-sm font-medium tracking-wide transition-all"
            >
              Időpontot foglalok <ArrowRight className="h-3.5 w-3.5" />
            </a>
          </div>
        ))}
        {FOURLIFE_PRODUCTS.map((p) => (
          <div key={p.key} className="bg-[#F6F3EB]/70 rounded-[1.75rem] p-7 border border-[#D4AF37]/30 flex flex-col">
            <div className="text-[10px] uppercase tracking-[0.25em] text-[#A88A2B]">4Life Megoldás</div>
            <h3 className="font-serif-display text-xl md:text-2xl text-[#1A2825] mt-2 leading-snug">{p.title}</h3>
            <p className="text-sm text-[#A88A2B] italic mt-1">{p.tagline}</p>
            <p className="mt-3 text-sm text-[#3A4D4A] font-light leading-relaxed flex-1">{p.description}</p>
            <Link
              to={`/4life#hl-${p.key}`}
              className="mt-5 inline-flex items-center justify-center gap-2 bg-[#A88A2B] hover:bg-[#8a6f1f] text-white px-5 py-2.5 rounded-full text-sm font-medium tracking-wide transition-all"
            >
              Megnézem a részleteket <ArrowRight className="h-3.5 w-3.5" />
            </Link>
          </div>
        ))}
      </div>
    </section>
  );
}

function Testimonials() {
  const { content } = useSiteContent();
  const items = content.testimonials || [];
  if (!items.length) return null;

  return (
    <section
      data-testid="home-testimonials"
      className="relative max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-24"
    >
      <div className="max-w-3xl">
        <span className="text-[11px] uppercase tracking-[0.3em] text-[#A88A2B] inline-flex items-center gap-2">
          <Quote className="h-3.5 w-3.5" /> Vendégeink szavai
        </span>
        <h2 className="font-serif-display text-4xl md:text-5xl font-light text-[#1A2825] mt-4 leading-[1.05]">
          Amit <em className="not-italic text-[#2A9D8F]">rólunk</em> mondanak
        </h2>
        <p className="mt-4 text-lg text-[#3A4D4A] font-light leading-relaxed">
          Valódi visszajelzések azoktól, akik nálunk töltötték fel magukat.
        </p>
      </div>

      <div className="mt-10 md:mt-14 grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-7">
        {items.map((t, i) => (
          <motion.article
            key={t.key || i}
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.7, delay: i * 0.1 }}
            data-testid={`testimonial-${t.key || i}`}
            className="relative bg-white rounded-[1.75rem] border border-[#EAE5D9] p-7 md:p-8 flex flex-col shadow-[0_8px_28px_rgba(26,40,37,0.04)]"
          >
            <Quote
              className="h-8 w-8 mb-4"
              style={{ color: (t.accent || "#2A9D8F") + "60" }}
            />
            <p className="text-[#3A4D4A] font-light leading-relaxed text-[15px] flex-1">
              {t.text}
            </p>
            <div className="mt-6 pt-5 border-t border-[#EAE5D9]">
              <div
                className="font-serif-display text-lg text-[#1A2825]"
                style={{ color: t.accent || "#1A2825" }}
              >
                {t.name}
              </div>
              {t.tag && (
                <div className="text-[10px] uppercase tracking-[0.25em] text-[#758C88] mt-1">
                  {t.tag}
                </div>
              )}
            </div>
          </motion.article>
        ))}
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true }}
        transition={{ duration: 0.7 }}
        className="mt-10 text-center max-w-2xl mx-auto"
      >
        <p className="text-sm text-[#3A4D4A] italic font-light">
          Szeretnéd Te is átélni? Foglalj időpontot vagy nézd meg a kezeléseinket.
        </p>
        <div className="mt-4 flex flex-col sm:flex-row items-center justify-center gap-3">
          <a
            href={BOOKING_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center gap-2 bg-[#2A9D8F] hover:bg-[#22857a] text-white px-6 py-3 rounded-full font-medium tracking-wide transition-all shadow-[0_10px_24px_rgba(42,157,143,0.32)] hover:-translate-y-0.5"
          >
            <MessageCircle className="h-4 w-4" /> Foglalok időpontot
          </a>
          <Link
            to="/masszazs"
            className="inline-flex items-center justify-center gap-2 border border-[#EAE5D9] hover:border-[#2A9D8F]/40 text-[#1A2825] px-6 py-3 rounded-full font-medium tracking-wide transition-all"
          >
            Kezelések áttekintése <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </motion.div>
    </section>
  );
}

function Contact() {
  const { content } = useSiteContent();
  const STUDIO = content.studio;
  return (
    <section
      id="kapcsolat"
      data-testid={HOME.contactSection}
      className="relative max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-24"
    >
      <div className="grid grid-cols-1 md:grid-cols-12 gap-10 items-center">
        <div className="md:col-span-6">
          <span className="text-[11px] uppercase tracking-[0.3em] text-[#A88A2B]">Kapcsolat</span>
          <h2 className="font-serif-display text-4xl md:text-5xl font-light text-[#1A2825] mt-4 leading-[1.05]">
            Találkozzunk —<br />
            <em className="not-italic text-[#2A9D8F]">személyesen</em> vagy online.
          </h2>
          <p className="mt-6 text-[#3A4D4A] font-light leading-relaxed max-w-md">
            Ha kérdésed van, vagy szeretnél időpontot foglalni egy regeneráló kezelésre — szólj.
            Boldogan válaszolok.
          </p>
        </div>
        <div className="md:col-span-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
          <a
            href={MESSENGER_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid={HOME.contactEmail}
            className="bg-white rounded-2xl p-6 border border-[#EAE5D9] hover:border-[#1877F2]/40 transition-colors flex flex-col"
          >
            <MessageCircle className="h-5 w-5 text-[#1877F2] mb-3" />
            <div className="text-[10px] uppercase tracking-[0.25em] text-[#758C88]">Messenger</div>
            <div className="text-[#1A2825] mt-1 font-medium">Üzenj most azonnal</div>
            <div className="mt-3 inline-flex items-center gap-1 text-xs text-[#1877F2]">
              Időpontfoglalás <ArrowRight className="h-3 w-3" />
            </div>
          </a>
          <a
            href={FACEBOOK_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid={HOME.contactFacebook}
            className="bg-white rounded-2xl p-6 border border-[#EAE5D9] hover:border-[#1877F2]/40 transition-colors flex flex-col"
          >
            <Facebook className="h-5 w-5 text-[#1877F2] mb-3" />
            <div className="text-[10px] uppercase tracking-[0.25em] text-[#758C88]">Facebook</div>
            <div className="text-[#1A2825] mt-1 font-medium">{STUDIO.name}</div>
            <div className="mt-3 inline-flex items-center gap-1 text-xs text-[#1877F2]">
              Kövess minket <ArrowRight className="h-3 w-3" />
            </div>
          </a>
          <div className="bg-[#F6F3EB]/70 rounded-2xl p-6 border border-[#D4AF37]/30 sm:col-span-2 flex flex-col gap-3">
            <div className="flex items-start gap-2.5">
              <MapPin className="h-4 w-4 flex-shrink-0 mt-0.5 text-[#A88A2B]" />
              <div>
                <div className="text-[10px] uppercase tracking-[0.25em] text-[#758C88]">Helyszín</div>
                <div className="text-[#1A2825] font-medium">{STUDIO.address}</div>
              </div>
            </div>
            <div className="flex items-start gap-2.5">
              <Phone className="h-4 w-4 flex-shrink-0 mt-0.5 text-[#A88A2B]" />
              <div className="flex-1">
                <div className="text-[10px] uppercase tracking-[0.25em] text-[#758C88]">Bejelentkezés és elérhetőségek</div>
                <ul className="mt-1 space-y-1">
                  {(STUDIO.practitioners || []).map((p) => (
                    <li key={p.phone} className="text-sm">
                      <span className="font-medium text-[#1A2825]">{p.name}</span>
                      <span className="text-[#A88A2B] mx-1.5">·</span>
                      <a href={`tel:${p.phone}`} className="text-[#1C6B61] hover:text-[#A88A2B]">
                        {p.phoneDisplay}
                      </a>
                    </li>
                  ))}
                </ul>
                <div className="mt-1.5 text-xs text-[#758C88]">{STUDIO.contactNote}</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export default function Home() {
  const [modalOpen, setModalOpen] = useState(false);
  const [pickedMagnet, setPickedMagnet] = useState(null);

  useEffect(() => {
    if (window.location.hash === "#feliratkozas") {
      const el = document.getElementById("magnetek");
      if (el) setTimeout(() => el.scrollIntoView({ behavior: "smooth" }), 80);
    }
  }, []);

  const openMagnet = (key) => {
    setPickedMagnet(key);
    setModalOpen(true);
  };

  return (
    <>
      <Hero />
      <Magnets onPick={openMagnet} />
      <ServicesPreview />
      <Testimonials />
      <MagnetModal open={modalOpen} magnetKey={pickedMagnet} onClose={() => setModalOpen(false)} />
    </>
  );
}
